// Shared cinematic form primitives for the BLV application forms.
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";

const inputCls =
  "w-full border border-silver/30 bg-onyx px-4 py-3 font-body font-medium text-cream outline-none transition-colors focus:border-gold placeholder:text-silver/35";

export const Field = ({ label, required, hint, children, testId }) => (
  <div data-testid={testId ? `field-wrap-${testId}` : undefined}>
    <label className="mb-1.5 block font-head text-sm font-extrabold uppercase tracking-wide text-cream">
      {label} {required && <span className="text-gold">*</span>}
    </label>
    {hint && <p className="mb-2 font-body text-xs text-silver/60">{hint}</p>}
    {children}
  </div>
);

export const TextInput = ({ value, onChange, testId, type = "text", required, placeholder }) => (
  <input
    type={type}
    required={required}
    value={value || ""}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testId}
    className={inputCls}
  />
);

export const TextArea = ({ value, onChange, testId, required, rows = 4, placeholder }) => (
  <textarea
    required={required}
    rows={rows}
    value={value || ""}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testId}
    className={inputCls}
  />
);

export const Select = ({ value, onChange, options, testId, required }) => (
  <select
    required={required}
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testId}
    className={`${inputCls} font-semibold`}
  >
    <option value="">Select...</option>
    {options.map((o) => (
      <option key={o} value={o}>{o}</option>
    ))}
  </select>
);

export const PillGroup = ({ value, onChange, options, testId, multi = false, format }) => {
  const selected = multi ? value || [] : value;
  const toggle = (o) => {
    if (multi) {
      onChange(selected.includes(o) ? selected.filter((x) => x !== o) : [...selected, o]);
    } else {
      onChange(o);
    }
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {options.map((o) => {
        const on = multi ? selected.includes(o) : selected === o;
        return (
          <button
            key={o}
            type="button"
            onClick={() => toggle(o)}
            data-testid={`${testId}-${o.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
            className={`border px-3 py-1.5 font-head text-xs font-extrabold uppercase tracking-wide transition-colors ${
              on
                ? "border-gold bg-gradient-to-b from-goldlight to-gold text-onyx"
                : "border-silver/30 bg-charcoal text-silver hover:border-gold/60 hover:text-goldlight"
            }`}
          >
            {format ? format(o) : o}
          </button>
        );
      })}
    </div>
  );
};

export const YesNo = ({ value, onChange, testId, third }) => (
  <PillGroup value={value} onChange={onChange} options={third ? ["Yes", "No", third] : ["Yes", "No"]} testId={testId} />
);

export const AckCheck = ({ checked, onChange, children, testId }) => (
  <label className="flex cursor-pointer items-start gap-3 border border-silver/25 bg-onyx p-3 transition-colors hover:border-gold/50">
    <input
      type="checkbox"
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
      data-testid={testId}
      className="mt-0.5 h-5 w-5 shrink-0 accent-[#C9A227]"
    />
    <span className="font-body text-sm leading-relaxed text-silver">{children}</span>
  </label>
);

export const SectionHead = ({ n, title, blurb }) => (
  <div className="mt-12 border-b border-gold/25 pb-3 first:mt-0">
    <div className="flex items-baseline gap-3">
      <span className="font-cinzel text-2xl text-gold">{n}</span>
      <h2 className="font-display text-2xl uppercase tracking-tight text-cream sm:text-3xl">{title}</h2>
    </div>
    {blurb && <p className="mt-1 font-body text-sm text-silver/60">{blurb}</p>}
  </div>
);

export const SubmitBar = ({ loading, label, loadingLabel, testId = "form-submit-btn" }) => (
  <button
    type="submit"
    disabled={loading}
    data-testid={testId}
    className="btn-sweep mt-10 flex w-full items-center justify-center gap-2 border border-gold bg-gradient-to-b from-goldlight to-gold px-6 py-4 font-head text-base font-extrabold uppercase tracking-[0.15em] text-onyx shadow-[0_0_30px_rgba(201,162,39,0.3)] transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
  >
    {loading ? loadingLabel : label}
  </button>
);

export const SuccessPanel = ({ title, id, note, emailNotice }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    className="gold-frame grain-dark relative bg-charcoal p-10 text-center"
    data-testid="form-success"
  >
    <div className="pointer-events-none absolute inset-2 border border-gold/20" />
    <CheckCircle2 className="mx-auto h-14 w-14 text-gold" />
    <h3 className="mt-5 font-display text-4xl uppercase tracking-tight text-metallic sm:text-5xl">{title}</h3>
    {id && (
      <p className="mx-auto mt-4 inline-block border border-gold/40 bg-onyx px-4 py-2 font-mono text-sm font-bold text-goldlight" data-testid="success-record-id">
        ID: {id}
      </p>
    )}
    <p className="mx-auto mt-4 max-w-md font-body text-base leading-relaxed text-silver">{note}</p>
    {emailNotice && (
      <p className="mx-auto mt-3 max-w-md font-body text-xs text-silver/60">
        A confirmation email is on its way. If it doesn't arrive, don't worry — your submission is safely saved.
      </p>
    )}
    <div className="gold-rule mx-auto mt-6 w-40" />
  </motion.div>
);

export const FormShell = ({ children, marker }) => (
  <div className="gold-frame grain-dark relative bg-charcoal p-6 sm:p-10">
    {marker && (
      <span className="absolute -top-4 right-6 -rotate-3 border border-gold/50 bg-onyx px-2.5 py-0.5 font-marker text-lg text-goldlight">{marker}</span>
    )}
    {children}
  </div>
);
